var __defProp = Object.defineProperty;
var __publicField = (obj, key, value) => {
  if (typeof key !== "symbol")
    key += "";
  if (key in obj)
    return __defProp(obj, key, {enumerable: true, configurable: true, writable: true, value});
  return obj[key] = value;
};
import * as Tone from "./_snowpack/pkg/tone.js";
export default class SSRecorder {
  constructor(video, filterCanvas) {
    __publicField(this, "drawMeter", () => {
      let W = this.meterCtx.width;
      let H = this.meterCtx.height;
      let meterGraident = this.meterCtx.createLinearGradient(0, 0, W, H);
      meterGraident.addColorStop(1, "#4feaae");
      meterGraident.addColorStop(0, "#f94678");
      const now = performance.now();
      let level = 0;
      if (this.analyser) {
        const dataArray = new Float32Array(this.analyser.fftSize);
        this.analyser.getFloatTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
        const rawLevel = Math.min(Math.sqrt(sum / dataArray.length) * 4, 1);
        if (rawLevel >= this._meterPeak) {
          this._meterPeak = rawLevel;
        } else {
          this._meterPeak = (this._meterPeak || 0) * 0.94;
        }
        level = this._meterPeak;
        // Peak hold dot
        if (rawLevel >= (this._peakHold || 0)) {
          this._peakHold = rawLevel;
          this._peakHoldTime = now;
        } else if (now - (this._peakHoldTime || 0) > 2000) {
          this._peakHold = (this._peakHold || 0) * 0.92;
        }
      }
      this.meterCtx.clearRect(0, 0, W, H);
      this.meterCtx.fillStyle = meterGraident;
      this.meterCtx.strokeStyle = "rgba(0, 0, 0, 0)";
      this.roundRect(this.meterCtx, 0, H * (1 - level) - 4, W, H * level + 4, W / 2, meterGraident);
      // Draw peak hold dot — color interpolated from gradient, dark shadow for contrast
      if (this._peakHold > 0.01) {
        const dotY = H * (1 - this._peakHold);
        const t = this._peakHold;
        const r = Math.round(79  + (249 - 79)  * t);
        const g = Math.round(234 + (70  - 234) * t);
        const b = Math.round(174 + (120 - 174) * t);
        this.meterCtx.save();
        this.meterCtx.shadowColor = 'rgba(0,0,0,0.85)';
        this.meterCtx.shadowBlur = 4;
        this.meterCtx.fillStyle = `rgb(${r},${g},${b})`;
        this.meterCtx.beginPath();
        this.meterCtx.arc(W / 2, dotY, W / 2 + 1, 0, Math.PI * 2);
        this.meterCtx.fill();
        this.meterCtx.restore();
      }
      return requestAnimationFrame(this.drawMeter);
    });
    this.filterCanvas = filterCanvas;
    this.video = video;
    const isFirefox = /Firefox/.test(navigator.userAgent);
    this.constraints = {
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: isFirefox, // Firefox mic level tanks when AGC is disabled
        channelCount: 1
      },
      video: {
        width: {ideal: 640},
        height: {ideal: 640},
        aspectRatio: 1,
        ratio: 1
      }
    };
    this.events = {};
    this.stream = null;
    this.videoTurningOn = false;
    this.chunks = [];
    this.newStream = null;
    this.filters = false;
    this.frameRate = 30;
    this.recording = false;
    let canvasMeter = document.getElementById("micMeter");
    this.meterCtx = canvasMeter.getContext("2d");
    this.meterCtx.width = canvasMeter.width;
    this.meterCtx.height = canvasMeter.height;
    this.firstTry = true;
    this.noCam = false;
    this.deleteTokens = false;
    this.rawAudioTrack = null;
    this.sources = {
      audio: [],
      video: []
    };
    if (localStorage.getItem("noCam")) {
      this.noCam = true;
    }
    if (localStorage.getItem("audioDevice")) {
      this.constraints.audio.deviceId = { exact: localStorage.getItem("audioDevice") };
    }
    if (localStorage.getItem("videoDevice")) {
      this.constraints.video.deviceId = { ideal: localStorage.getItem("videoDevice") };
    }
  }
  getMedia() {
    setTimeout(() => {
      if (this.permissionGranted == false) {
        this.dispatchEvent("show_message", {type: "info", message: "We're still waiting to access your microphone (and camera if you use one)"});
      }
    }, 3e4);
    if (this.stream) {
      this.stopStream();
    }
    if (this.noCam) {
      delete this.constraints.video;
    }
    navigator.mediaDevices.getUserMedia(this.constraints).then(async (stream) => {
      if (this.deleteTokens) {
        localStorage.removeItem("audioDevice");
        localStorage.removeItem("videoDevice");
      }
      localStorage.setItem("audioDevice", stream.getAudioTracks()[0].getSettings().deviceId);
      if (!this.noCam) {
        this.frameRate = stream.getVideoTracks()[0].getSettings().frameRate;
        this.dispatchEvent("clearCanvas");
        localStorage.setItem("videoDevice", stream.getVideoTracks()[0].getSettings().deviceId);
        localStorage.removeItem("noCam");
      } else {
        localStorage.setItem("noCam", "true");
      }
      if (typeof this.constraints.video == "undefined") {
        this.noCam = true;
      }
      this.stream = stream;
      // Store the raw hardware track — applyBoostChain() always routes from here.
      this.rawAudioTrack = stream.getAudioTracks()[0];
      if (this.boostGain) { try { this.boostGain.disconnect(); } catch {} this.boostGain = null; }
      if (this.boostSrc)  { try { this.boostSrc.disconnect();  } catch {} this.boostSrc  = null; }
      // Resuming Tone's AudioContext here unlocks Firefox's per-origin autoplay policy,
      // which also allows the Superpowered AudioContext to run on the same page.
      try { await Tone.getContext().rawContext.resume(); } catch(e) {}
      this.permissionGranted = true;
      this.video.srcObject = this.stream;
      this.initRecorder();
      this.initInputDevices();
      this.grabInputs();
    }).catch((err) => {
      let error = {
        type: "error",
        message: err
      };
      this.errorMessage = err;
      console.error("Error with attempt to load inputs: ", err);
      this.dispatchEvent("hide_canvas");
      if (!this.noCam) {
        if (this.constraints.video && this.constraints.video.deviceId) {
          delete this.constraints.video.deviceId;
          this.deleteTokens = true;
          this.getMedia();
        } else if (typeof this.constraints.audio.deviceId !== "undefined") {
          delete this.constraints.audio.deviceId;
          this.deleteTokens = true;
          this.getMedia();
        } else {
          error.type = "info";
          error.message = "No Camera found! Switching to Audio Only mode.";
          this.dispatchEvent("show_message", error);
          this.noCam = true;
          this.getMedia();
        }
      } else {
        if (typeof this.constraints.audio.deviceId != "undefined") {
          if (typeof this.constraints.video != "undefined") {
            if (typeof this.constraints.video.deviceId != "undefined") {
              delete this.constraints.video.deviceId;
              this.noCam = true;
            }
          }
          delete this.constraints.audio.deviceId;
          this.deleteTokens = true;
          this.getMedia();
        } else {
          this.dispatchEvent("no_permissions", this.errorMessage);
        }
      }
    });
  }
  initInputDevices() {
    if (this.analyserSource) {
      this.analyserSource.disconnect();
      this.analyserSource = null;
    }
    const rawCtx = Tone.getContext().rawContext;
    this.analyser = rawCtx.createAnalyser();
    this.analyser.fftSize = 256;
    if (this.boostGain) {
      // boostGain is already in rawCtx — connect directly to avoid a circular
      // MediaStream bridge (dest track → createMediaStreamSource in same context stalls Chrome)
      this.boostGain.connect(this.analyser);
    } else {
      const audioStream = new MediaStream(this.stream.getAudioTracks());
      this.analyserSource = rawCtx.createMediaStreamSource(audioStream);
      this.analyserSource.connect(this.analyser);
    }
    requestAnimationFrame(this.drawMeter);
  }
  initRecorder() {
    let options = {
      audioBitsPerSecond: 128e3,
      videoBitsPerSecond: 15e5
    };
    let isSupported = MediaRecorder.isTypeSupported;
    if (isSupported("video/mp4")) {
      this.mimeType = "video/mp4";
    } else if (isSupported("video/webm;codecs=h264")) {
      this.mimeType = "video/webm; codecs=h264";
    } else if (isSupported("video/webm;codecs=vp9")) {
      this.mimeType = "video/webm; codecs=vp9";
    } else if (isSupported("video/webm;codecs=vp8")) {
      this.mimeType = "video/webm; codecs=vp8";
    }
    if (!this.newStream) {
      this.recorder = new MediaRecorder(this.stream, options);
    } else {
      this.recorder = new MediaRecorder(this.newStream, options);
    }
    this.recorder.ondataavailable = (e) => {
      this.chunks.push(e.data);
    };
    // Transition to review on stop (fires once), matching the filtersOn path —
    // rather than from ondataavailable. Equivalent today (start() has no
    // timeslice so dataavailable fires once), but correct/consistent and safe
    // if a timeslice is ever added.
    this.recorder.onstop = (e) => {
      this.goToReview();
    };
  }
  filtersOn() {
    this.recorder = null;
    this.newStream = this.filterCanvas.captureStream(this.frameRate);
    this.newStream.addTrack(this.stream.getAudioTracks()[0]);
    let options = {
      audioBitsPerSecond: 128e3,
      videoBitsPerSecond: 15e5
    };
    let isSupported = MediaRecorder.isTypeSupported;
    if (isSupported("video/mp4")) {
      this.mimeType = "video/mp4";
    } else if (isSupported("video/webm;codecs=h264")) {
      this.mimeType = "video/webm; codecs=h264";
    } else if (isSupported("video/webm;codecs=vp9")) {
      this.mimeType = "video/webm; codecs=vp9";
    } else if (isSupported("video/webm;codecs=vp8")) {
      this.mimeType = "video/webm; codecs=vp8";
    }
    this.recorder = new MediaRecorder(this.newStream, options);
    this.recorder.ondataavailable = (e) => {
      this.chunks.push(e.data);
    };
    this.recorder.onstop = (e) => {
      this.goToReview();
    };
  }
  filtersOff() {
    this.recorder = null;
    this.newStream = null;   // audio-only from here — don't record the stale filter-canvas stream
    this.initRecorder();
  }
  startAutoCalibration(onTick, onDone, target = 0.5) {
    if (!this.analyser) { onDone(null, 'Microphone not ready.'); return; }
    const TARGET   = target;   // target meter level (0–1 scale, where 1 = full)
    const DURATION = 4000;  // ms to sample
    const POLL_MS  = 80;
    let maxLevel = 0;
    const start = performance.now();
    const tick = () => {
      const elapsed  = performance.now() - start;
      const progress = Math.min(elapsed / DURATION, 1);
      const dataArray = new Float32Array(this.analyser.fftSize);
      this.analyser.getFloatTimeDomainData(dataArray);
      let peak = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const abs = Math.abs(dataArray[i]);
        if (abs > peak) peak = abs;
      }
      const level = Math.min(peak, 1);
      if (level > maxLevel) maxLevel = level;
      onTick(progress, level);
      if (progress < 1) { setTimeout(tick, POLL_MS); return; }
      if (maxLevel < 0.01) {
        onDone(null, 'No signal detected. Speak louder or check your microphone.');
        return;
      }
      // Compute gain needed to bring the measured peak to TARGET.
      // currentBoost accounts for any gain already in the chain.
      const currentBoost = this.boostGain ? this.boostGain.gain.value : 1;
      let newBoost = currentBoost * (TARGET / maxLevel);
      newBoost = Math.max(0.1, Math.min(newBoost, 40)); // allow attenuation down to 0.1× (-20dB)
      this.applyBoostChain(newBoost).then(() => {
        onDone(newBoost, maxLevel);
      }).catch(() => {
        onDone(newBoost, maxLevel);
      });
    };
    setTimeout(tick, POLL_MS);
  }
  async applyBoostChain(value) {
    if (!this.stream || !this.rawAudioTrack || this.rawAudioTrack.readyState !== 'live') {
      console.warn('applyBoostChain: stream or raw track not available');
      return;
    }
    const rawCtx = Tone.getContext().rawContext;
    await rawCtx.resume();
    // Tear down any previous chain
    if (this.boostGain) { try { this.boostGain.disconnect(); } catch {} this.boostGain = null; }
    if (this.boostSrc)  { try { this.boostSrc.disconnect();  } catch {} this.boostSrc  = null; }
    if (this.analyserSource) { try { this.analyserSource.disconnect(); } catch {} this.analyserSource = null; }
    const src      = rawCtx.createMediaStreamSource(new MediaStream([this.rawAudioTrack]));
    const gainNode = rawCtx.createGain();
    gainNode.gain.value = value;
    const dest = rawCtx.createMediaStreamDestination();
    src.connect(gainNode);
    gainNode.connect(dest);
    const boostedTrack = dest.stream.getAudioTracks()[0];
    if (!boostedTrack) throw new Error('destination produced no audio track');
    this.boostSrc  = src;
    this.boostGain = gainNode;
    // Swap the stream's audio track so MediaRecorder picks up the gain
    this.stream.getAudioTracks().forEach(t => this.stream.removeTrack(t));
    this.stream.addTrack(boostedTrack);
    // Keep the recorded stream (camera/filters path) in sync. MediaRecorder
    // captures this.newStream, whose audio track was snapshotted once in
    // filtersOn(). Without this, a boost change swaps this.stream but leaves
    // newStream holding a stale — and now source-disconnected, i.e. silent —
    // track, so the saved recording has NO vocals. Only touch it while idle
    // (matches the initRecorder guard below); never mutate an active capture.
    if (this.newStream && !this.recording) {
      this.newStream.getAudioTracks().forEach(t => this.newStream.removeTrack(t));
      this.newStream.addTrack(boostedTrack);
    }
    // Reconnect the meter analyser to read the post-gain signal
    if (this.analyser) gainNode.connect(this.analyser);
    // Reinitialise recorder with the updated stream track (only when idle)
    if (!this.recording) this.initRecorder();
    localStorage.setItem('ssMicBoost', String(value));
    this.dispatchEvent('boostApplied', value);
  }
  removeBoostChain() {
    if (!this.stream || !this.rawAudioTrack || this.rawAudioTrack.readyState !== 'live') return;
    if (this.boostGain) { try { this.boostGain.disconnect(); } catch {} this.boostGain = null; }
    if (this.boostSrc)  { try { this.boostSrc.disconnect();  } catch {} this.boostSrc  = null; }
    this.stream.getAudioTracks().forEach(t => this.stream.removeTrack(t));
    this.stream.addTrack(this.rawAudioTrack);
    // Keep the recorded (camera/filters) stream in sync with the raw track too,
    // so removing the boost doesn't leave newStream capturing a silent track.
    if (this.newStream && !this.recording) {
      this.newStream.getAudioTracks().forEach(t => this.newStream.removeTrack(t));
      this.newStream.addTrack(this.rawAudioTrack);
    }
    if (this.analyser) {
      if (this.analyserSource) { try { this.analyserSource.disconnect(); } catch {} }
      const rawCtx = Tone.getContext().rawContext;
      this.analyserSource = rawCtx.createMediaStreamSource(new MediaStream([this.rawAudioTrack]));
      this.analyserSource.connect(this.analyser);
    }
    if (!this.recording) this.initRecorder();
    this.dispatchEvent('boostApplied', 1);
  }
  start() {
    if (!this.recorder) {
      this.dispatchEvent("show_message", {type: "info", message: "Your mic/camera isn't ready yet — please wait a moment and try again."});
      return false;
    }
    this.recording = true;
    this.recorder.start();
    return true;
  }
  stop() {
    if (this.recorder.state != "inactive") {
      this.recorder.stop();
      this.recording = false;
    }
  }
  stopStream() {
    this.video.srcObject = null;
    if (this.boostGain) { try { this.boostGain.disconnect(); } catch {} this.boostGain = null; }
    if (this.boostSrc)  { try { this.boostSrc.disconnect();  } catch {} this.boostSrc  = null; }
    if (this.analyserSource) {
      this.analyserSource.disconnect();
      this.analyserSource = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }
  grabInputs() {
    this.sources = {
      audio: [],
      video: []
    };
    navigator.mediaDevices.enumerateDevices().then((result) => {
      result.forEach((input) => {
        if (input.kind === "audioinput") {
          this.sources.audio.push(input);
        } else if (input.kind === "videoinput") {
          this.sources.video.push(input);
        }
      });
      this.dispatchEvent("input_sources_ready", this.sources);
      if (!this.noCam) {
        this.dispatchEvent("filtersOn");
      }
    });
  }
  changeAudioDevice(deviceId) {
    this.constraints.audio.deviceId = {
      exact: deviceId
    };
    this.getMedia();
  }
  changeVideoDevice(deviceId) {
    if (typeof this.constraints.video == "undefined") {
      this.constraints.video = {
        width: {ideal: 640},
        height: {ideal: 640},
        aspectRatio: 1,
        ratio: 1
      };
    }
    this.constraints.video.deviceId = {
      exact: deviceId
    };
    this.noCam = false;
    this.getMedia();
  }
  videoOff() {
    this.noCam = true;
    this.recorder = null;
    // Drop the filter-canvas capture stream so the next (audio-only) recording
    // captures the clean audio stream, not the stale video newStream. Otherwise
    // the recorded blob still has a (frozen) video track, loadVoice() can't
    // decode it, voiceReady/initReview never fire, and the recorder gets stuck
    // in practicing instead of review.
    this.newStream = null;
    this.dispatchEvent("filtersOff");
    this.getMedia();
  }
  videoOn() {
    this.noCam = false;
    if (typeof this.constraints.video == "undefined") {
      this.constraints.video = {
        width: {ideal: 640},
        height: {ideal: 640},
        aspectRatio: 1,
        ratio: 1
      };
    }
    this.videoTurningOn = true;
    this.getMedia();
  }
  goToReview() {
    if (this.stream) {
      this.stopStream();
    }
    this.blob = new Blob(this.chunks, {type: this.mimeType});
    this.dispatchEvent("blobDone", true);
  }
  addEventListener(eventName, cb) {
    if (!this.events[eventName]) {
      this.events[eventName] = new Array();
    }
    this.events[eventName].push(cb);
  }
  dispatchEvent(event, args) {
    if (!this.events[event])
      return;
    this.events[event].forEach((cb) => cb(event, args));
  }
  roundRect(ctx, x, y, width, height, radius, fill, stroke) {
    if (typeof stroke == "undefined") {
      stroke = true;
    }
    if (typeof radius === "undefined") {
      radius = 5;
    }
    if (typeof radius === "number") {
      radius = {tl: radius, tr: radius, br: radius, bl: radius};
    } else {
      let defaultRadius = {tl: 0, tr: 0, br: 0, bl: 0};
      for (let side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
    if (fill) {
      ctx.fill();
    }
    if (stroke) {
      ctx.stroke();
    }
  }
}
